import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

import { Button, type ButtonProps, Tooltip } from "@mui/material";

/**
 * The muted look for a blocked row action button, pulled from the theme's
 * disabled tokens rather than a hex literal so it tracks the theme (including
 * dark mode). Shared by every RowActionButton instance so the look can't
 * drift between the three buttons.
 */
const BLOCKED_ROW_ACTION_SX: SxProps<Theme> = {
  color: "text.disabled",
  borderColor: "action.disabled",
};

/**
 * A row action button that stays reachable by keyboard and screen reader even
 * while blocked. A native `disabled` button (what MUI's `Button` renders by
 * default) drops out of the tab order, so a keyboard or screen-reader user
 * could never land on it to hear why it's unavailable. Instead this keeps the
 * button focusable and marks the blocked state with `aria-disabled`, guards
 * the click handler to match, and applies the muted look manually since
 * `aria-disabled` styles nothing on its own.
 *
 * `describeChild` keeps the button's own text as its accessible name — without
 * it, MUI's `Tooltip` would overwrite the name with the reason text instead of
 * exposing it as an `aria-describedby`, and every "clicked X, then Y" test
 * query by accessible name would stop matching.
 */
export function RowActionButton({
  blocked,
  reason,
  onClick,
  startIcon,
  variant = "outlined",
  sx,
  children,
}: {
  blocked: boolean;
  reason: string;
  onClick: () => void;
  startIcon?: ReactNode;
  variant?: ButtonProps["variant"];
  sx?: SxProps<Theme>;
  children: ReactNode;
}) {
  return (
    <Tooltip title={reason} describeChild>
      <Button
        size="small"
        variant={variant}
        startIcon={startIcon}
        aria-disabled={blocked}
        onClick={() => {
          if (blocked) return;
          onClick();
        }}
        sx={blocked ? BLOCKED_ROW_ACTION_SX : sx}
      >
        {children}
      </Button>
    </Tooltip>
  );
}
